import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, CornerDownLeft, Loader2, Search } from 'lucide-react';
import { NAVIGATION } from '@bruhat/shared';
import { cn } from '@/lib/cn';
import { api } from '@/lib/api-client';
import { useUiStore } from '@/store/ui.store';
import { useAuthStore } from '@/store/auth.store';
import { useDebounce } from '@/hooks/use-debounce';
import { Money } from '@/components/ui';

interface SearchHit {
  type: string;
  id: string;
  title: string;
  subtitle: string | null;
  amount: string | null;
  href: string;
  related?: Array<{ type: string; label: string; href: string }>;
}

interface SearchResponse {
  query: string;
  totalHits: number;
  groups: Array<{ type: string; label: string; hits: SearchHit[] }>;
}

interface Item {
  id: string;
  title: string;
  subtitle: string | null;
  amount: string | null;
  href: string;
  group: string;
  related?: SearchHit['related'];
}

/**
 * ⌘K command bar.
 *
 * Searching a unit number such as "A-203" surfaces the unit AND the thread
 * hanging off it — project, customer, booking and statement — so one keystroke
 * reaches the whole story rather than a single row.
 */
export function CommandPalette() {
  const open = useUiStore((s) => s.commandPaletteOpen);
  const setOpen = useUiStore((s) => s.setCommandPaletteOpen);
  const companyId = useAuthStore((s) => s.companyId);
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const debouncedQuery = useDebounce(query, 220);

  // Global shortcut.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [setOpen]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setResults(null);
      setHighlighted(0);
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !companyId || debouncedQuery.trim().length < 2) {
      setResults(null);
      return;
    }

    let cancelled = false;
    setLoading(true);

    api
      .get<SearchResponse>('/search', { query: { q: debouncedQuery, limit: 6 } })
      .then((data) => {
        if (!cancelled) setResults(data);
      })
      .catch(() => {
        if (!cancelled) setResults(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, open, companyId]);

  // Navigation destinations double as palette entries when nothing matches.
  const navItems = useMemo<Item[]>(() => {
    const term = query.trim().toLowerCase();
    const items: Item[] = [];
    for (const section of NAVIGATION) {
      if (section.path) {
        items.push({
          id: section.path,
          title: section.label,
          subtitle: 'Go to',
          amount: null,
          href: section.path,
          group: 'Navigate',
        });
      }
      for (const child of section.children ?? []) {
        items.push({
          id: child.path,
          title: child.label,
          subtitle: section.label,
          amount: null,
          href: child.path,
          group: 'Navigate',
        });
      }
    }
    if (!term) return items.slice(0, 8);
    return items.filter((item) => item.title.toLowerCase().includes(term)).slice(0, 6);
  }, [query]);

  const items = useMemo<Item[]>(() => {
    const recordItems: Item[] =
      results?.groups.flatMap((group) =>
        group.hits.map((hit) => ({
          id: `${hit.type}:${hit.id}`,
          title: hit.title,
          subtitle: hit.subtitle,
          amount: hit.amount,
          href: hit.href,
          group: group.label,
          related: hit.related,
        })),
      ) ?? [];

    return [...recordItems, ...navItems];
  }, [results, navItems]);

  useEffect(() => {
    setHighlighted(0);
  }, [items.length]);

  if (!open) return null;

  const go = (href: string) => {
    navigate(href);
    setOpen(false);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlighted((i) => Math.min(i + 1, items.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlighted((i) => Math.max(i - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const item = items[highlighted];
      if (item) go(item.href);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  };

  // Group headings while keeping one flat index for keyboard movement.
  let renderIndex = -1;
  const grouped = items.reduce<Record<string, Item[]>>((acc, item) => {
    (acc[item.group] ??= []).push(item);
    return acc;
  }, {});

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-start justify-center p-4 pt-[10vh]">
      <div
        className="absolute inset-0 animate-fade-in bg-ink/40 backdrop-blur-[2px]"
        onClick={() => setOpen(false)}
        aria-hidden
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label="Search"
        className="relative w-full max-w-2xl overflow-hidden rounded-xl border border-surface-border bg-white shadow-raised dark:border-night-border dark:bg-night-raised"
      >
        <div className="flex items-center gap-2 border-b border-surface-border px-4 dark:border-night-border">
          <Search className="h-4 w-4 shrink-0 text-ink-subtle" />
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search a unit, customer, voucher, project, vendor…"
            className="h-12 w-full bg-transparent text-sm text-ink outline-none placeholder:text-ink-subtle dark:text-slate-100"
          />
          {loading && <Loader2 className="h-4 w-4 shrink-0 animate-spin text-brand-500" />}
        </div>

        <div className="max-h-[55vh] overflow-y-auto py-1">
          {items.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-ink-muted dark:text-slate-400">
              {query.trim().length < 2
                ? 'Type at least two characters to search.'
                : `Nothing matched “${query}”.`}
            </p>
          ) : (
            Object.entries(grouped).map(([group, groupItems]) => (
              <div key={group}>
                <p className="px-4 pb-1 pt-2 text-2xs font-semibold uppercase tracking-wider text-ink-subtle">
                  {group}
                </p>
                {groupItems.map((item) => {
                  renderIndex += 1;
                  const index = renderIndex;
                  return (
                    <div key={item.id}>
                      <button
                        onMouseEnter={() => setHighlighted(index)}
                        onClick={() => go(item.href)}
                        className={cn(
                          'flex w-full items-center gap-3 px-4 py-2 text-left',
                          highlighted === index
                            ? 'bg-brand-50 dark:bg-brand-900/25'
                            : 'hover:bg-surface-muted dark:hover:bg-night-sunken',
                        )}
                      >
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium text-ink dark:text-slate-100">
                            {item.title}
                          </span>
                          {item.subtitle && (
                            <span className="block truncate text-2xs text-ink-subtle">
                              {item.subtitle}
                            </span>
                          )}
                        </span>
                        {item.amount && (
                          <Money value={item.amount} compact className="text-xs text-ink-muted" />
                        )}
                        {highlighted === index && (
                          <CornerDownLeft className="h-3.5 w-3.5 shrink-0 text-brand-500" />
                        )}
                      </button>

                      {/* The thread around a record — one hop to the money. */}
                      {highlighted === index && item.related && item.related.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 bg-brand-50/50 px-4 pb-2 dark:bg-brand-900/10">
                          {item.related.map((related) => (
                            <button
                              key={related.href}
                              onClick={(event) => {
                                event.stopPropagation();
                                go(related.href);
                              }}
                              className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-2xs font-medium text-brand-700 shadow-sm hover:bg-brand-100 dark:bg-night-raised dark:text-brand-200"
                            >
                              {related.label}
                              <ArrowRight className="h-2.5 w-2.5" />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>

        <div className="flex items-center gap-3 border-t border-surface-border px-4 py-2 text-2xs text-ink-subtle dark:border-night-border">
          <span className="flex items-center gap-1">
            <kbd className="kbd">↑</kbd>
            <kbd className="kbd">↓</kbd> navigate
          </span>
          <span className="flex items-center gap-1">
            <kbd className="kbd">↵</kbd> open
          </span>
          <span className="flex items-center gap-1">
            <kbd className="kbd">esc</kbd> close
          </span>
        </div>
      </div>
    </div>,
    document.body,
  );
}
